import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { boathouse } from '../data/boathouse';
const MotionLink = motion(Link);
interface Slide {
  word: string;
  image: string;
  caption?: {
    project: string;
    detail: string;
    to: string;
  };
}
const slides: Slide[] = [{
  word: 'Seven Islands',
  image: 'https://cdn.magicpatterns.com/uploads/rf3W9GGHYNhNp7V6syNuJH/7islands-page1.png',
  caption: {
    project: 'Seven Islands',
    detail: 'Cape Coral, Florida · Coming March 2027',
    to: '/projects/7-islands'
  }
}, {
  word: 'the Boathouse',
  image: boathouse.heroImage,
  caption: {
    project: 'Boathouse',
    detail: `${boathouse.subtitle} · ${boathouse.status}`,
    to: boathouse.slug
  }
}, {
  word: 'Communities',
  image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=1920&q=80'
}, {
  word: 'Opportunities',
  image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1920&q=80'
}, {
  word: 'Investments',
  image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80'
}, {
  word: 'Southwest Florida',
  image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80'
}];
export function HeroSlideshow() {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  return <section className="relative min-h-screen w-full overflow-hidden bg-black" data-id="element-120" data-mp="3nw1akofest6g">
      {/* Crossfading imagery */}
      <AnimatePresence data-id="element-121" data-mp="3suunaemvgg5k">
        <motion.div key={current} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1,
        ease: 'linear'
      }} className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url(${slides[current].image})`
      }} aria-hidden="true" data-id="element-122" data-mp="1m22188luklwe" />
      </AnimatePresence>
      <div className="absolute inset-0 bg-black/70" aria-hidden="true" data-id="element-123" data-mp="1fm1suxqi1ifd" />

      {/* Statement */}
      <div className="relative z-10 min-h-screen flex items-start pt-[50px] pb-24" data-id="element-124" data-mp="1bkzu0i0hv8dx">
        <div className="max-w-7xl w-full mx-auto px-6 md:px-10" data-id="element-125" data-mp="28azcb46g5l9p">
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="text-[#C5A55A] text-xs md:text-sm tracking-[0.35em] uppercase mb-8" data-id="element-126" data-mp="28ah91nlda6sa">
            Real Estate Development
          </motion.p>

          <h1 className="font-serif text-white leading-[0.95]" data-id="element-127" data-mp="2r1gr04ug2gc2">
            <motion.span initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: 0.06,
            ease: [0.23, 1, 0.32, 1]
          }} className="block text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight" data-id="element-128" data-mp="2r2rtm1c5li1l">
              Sinclair Develops
            </motion.span>

            {/* Rotating word — font size lives on the clip box so its height
                matches the text exactly */}
            <span className="relative block mt-3 md:mt-5 text-[2rem] sm:text-5xl md:text-6xl lg:text-7xl h-[1.35em] overflow-hidden" aria-live="polite" data-id="element-129" data-mp="rrhzq2w4bhy9">
              <AnimatePresence mode="wait" initial={false} data-id="element-130" data-mp="11h1kxf7aznwa">
                <motion.span key={slides[current].word} initial={{
                opacity: 0,
                y: '40%'
              }} animate={{
                opacity: 1,
                y: '0%'
              }} exit={{
                opacity: 0,
                y: '-40%'
              }} transition={{
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} className="absolute left-0 top-0 block whitespace-nowrap leading-[1.25] italic text-[#C5A55A]" data-id="element-131" data-mp="11hkykjpx7q4i">
                  {slides[current].word}.
                </motion.span>
              </AnimatePresence>
            </span>
          </h1>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: 0.14,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-10 max-w-xl" data-id="element-132" data-mp="1bibzaly96uh5">
            <div className="w-16 h-[2px] bg-[#C5A55A] mb-6" data-id="element-133" data-mp="dx6huew8pf2v" />
            <p className="text-xl md:text-2xl text-white font-light mb-2" data-id="element-134" data-mp="ssvydxinp007">
              We see what a property can become.
            </p>
            <p className="text-sm md:text-base text-gray-300" data-id="element-135" data-mp="nt9ljp6qplrx">
              Commercial · Residential · Site · Redevelopment
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-10 flex flex-wrap items-center gap-4" data-id="element-136" data-mp="1z9r54op8ntx5">
            <a href="#developments" className="inline-flex items-center gap-2 bg-[#C5A55A] hover:bg-[#D4AF37] text-black px-8 py-4 font-medium rounded-sm transition-colors duration-200" data-id="element-137" data-mp="1xt3uwuq7pjy0">
              See the developments
              <ArrowRight className="w-5 h-5" data-id="element-138" data-mp="2uibg21mx5r1e" />
            </a>
            <Link to="/contact" className="inline-flex items-center border border-white/40 hover:border-[#C5A55A] hover:text-[#C5A55A] text-white px-8 py-4 font-medium rounded-sm transition-colors duration-200" data-id="element-139" data-mp="2uhjmewcvu3dr">
              Bring us an opportunity
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Indicators */}
      <div className="absolute bottom-10 left-6 md:left-10 flex gap-3 z-10" data-id="element-140" data-mp="3d9m6yisna3ta">
        {slides.map((slide, index) => <button key={slide.word} onClick={() => setCurrent(index)} className={`h-[3px] transition-all duration-200 ${index === current ? 'bg-[#C5A55A] w-12' : 'bg-white/40 hover:bg-white w-6'}`} aria-label={`Show ${slide.word}`} data-id="element-141" data-mp="3n403ta0ruh3f" data-mpi={index} />)}
      </div>

      {/* Project caption for slides that show a specific development */}
      <AnimatePresence mode="wait" data-id="element-142" data-mp="2zeozjik7q4kj">
        {slides[current].caption && <MotionLink to={slides[current].caption?.to ?? '/projects'} key={slides[current].word} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute bottom-8 right-6 md:right-10 z-10 text-right border-r-2 border-[#C5A55A] pr-4 hover:opacity-80 transition-opacity" data-id="element-143" data-mp="2vdmzizih1ohi">
            <p className="text-white font-serif text-lg md:text-xl" data-id="element-144" data-mp="26eiiv5ajdx4f">
              {slides[current].caption?.project}
            </p>
            <p className="text-gray-300 text-xs md:text-sm tracking-wide" data-id="element-145" data-mp="2gavcy49zuczp">
              {slides[current].caption?.detail}
            </p>
          </MotionLink>}
      </AnimatePresence>
    </section>;
}